import BinDTO from "src/models/BinDTO";
import { BaseChartProps, OPICableChart, TemperatureCableChart, TemperatureCableOpiChart, useTemperatureCableSensorsChartHistoryQueryOptions as useTemperatureCableSensorChartHistoryQueryOptions } from "./TemperatureCableChart";
import React, { useCallback, useMemo, useState } from "react";
import { RedoOutlined } from "@ant-design/icons";
import { useIsFetching, useQuery } from "@tanstack/react-query";
import { Space, Button, SelectProps, Select, Switch } from "antd";
import dayjs from "dayjs";
import { binDBKeys } from "src/pages/binOverview/BinCommander";
import { RangeValue } from "./ChartContainer";
import { ChartDatePicker } from "./Co2Chart";
import { LayerTemperatureHistoryChart, useTemperatureLayerSummaryChartHistoryQueryOptions } from "./LayerTemperatureHistoryChart";
import { ExtraCharts } from "./DryingDashboard";
import TemperatureSensorEnum from "src/consts/TemperatureSensorEnum";
import { BaseCsv, useAddDefaultAnnotationsToCsv } from "./modehistoryAndFanChangeHistory";
import { useFanHeaterChangeHistoryQueryOptions } from "./shared";
import { CSVLink } from "react-csv";
import { useAmbientPlenumTemperatureQueryOptions } from "./AmbientPlenumChart";
import { BaseHeaders } from "./csv/types";
import { chartCsvHeaders, timestampHeader } from "./csv/core";
import { CsvButton } from "./csv/CsvButton";
import { useBinDTOContext } from "src/queries/BinDTOContext";

export const TemperatureCableChartTab = (props: BaseChartProps & { binDTO: BinDTO }) => {

  const bindtoContext = useBinDTOContext();
  const useRHForFanControl = bindtoContext.binDTO?.useRHForFanControl;
  const currentDate = dayjs();
  const priorDate = currentDate.subtract(14, 'days');


  const isControlled = typeof props.value != 'undefined';
  const [internalValue, setIntervalValue] = useState<RangeValue>([priorDate, currentDate]);
  const inprogress = useIsFetching([...binDBKeys.bin(props.binId), "charts"]);

  // Internally, we need to deal with some value. Depending on whether
  // the component is controlled or not, that value comes from its
  // props or from its internal state.

  const value = isControlled ? props.value : internalValue;

  const onDateChange = useCallback((values: RangeValue, formatter?: any) => {
    if (props.onChange) {
      props.onChange(values);
    }
    else {
      setIntervalValue(values);
    }
  }, [props.onChange]);


  const [extraCharts, setExtraCharts] = useState<Array<ExtraCharts>>([]);

  const hasOpiSensors = (props.binDTO?.opiMoistureCables?.length ?? 0) > 0;
  const hasTemperatureCables = (props.binDTO?.temperatureCables?.length ?? 0) > 0;
  const preferredTemperatureSource = props.binDTO?.temperatureSensorsType ?? TemperatureSensorEnum.Thermocouple;


  const extraChartSelectOptions = useMemo(() => {
    const extraChartSelectOptions: SelectProps['options'] = [];

    if (hasTemperatureCables) {
      extraChartSelectOptions.push({
        label: "Temp (per sensor)",
        value: ExtraCharts.TemperatureCables,
      })
    }
    else if (hasOpiSensors) {
      extraChartSelectOptions.push({
        label: "OPI Temp (per sensor)",
        value: ExtraCharts.OPITemperatureSensors,
      })
    }

    return extraChartSelectOptions;
  }, [hasOpiSensors, hasTemperatureCables]);



  const [showPerSensorChart, setShowPerSensorChart] = useState(false);

  const showTemperatureChartByLayer = !showPerSensorChart;


  const ambientPlenumTemperatureChartQueryOptions = useAmbientPlenumTemperatureQueryOptions({
    binId: props.binId,
    range: value,
  });
  const ambientPlenumTemperatureChartQuery = useQuery({
    ...ambientPlenumTemperatureChartQueryOptions,
  });

  const temperatureCableChartHistoryQueryOptions = useTemperatureCableSensorChartHistoryQueryOptions({
    binId: props.binId,
    range: value,
  });

  const layerSummaryTemperatureHistoryQueryOptions = useTemperatureLayerSummaryChartHistoryQueryOptions({
    binId: props.binId,
    range: value,
  });
  const temperatureCableIndividualSensorChartHistoryQuery = useQuery({ ...temperatureCableChartHistoryQueryOptions, enabled: !showTemperatureChartByLayer });
  const layerSummaryTemperatureHistoryQuery = useQuery({ ...layerSummaryTemperatureHistoryQueryOptions, enabled: showTemperatureChartByLayer });


  interface Return {
    headers: BaseHeaders,
    data: Array<BaseCsv>
  }

  function flattenToCsv(): Return {

    const layerCsv = () => {
      const headers: BaseHeaders = [timestampHeader];
            
            headers.push(chartCsvHeaders.fanPlenumTempF);
            headers.push(chartCsvHeaders.fanPlenumRH);
            headers.push(chartCsvHeaders.fanPlenumAH);
            headers.push(chartCsvHeaders.fanPlenumDP);
            if (useRHForFanControl === false) {
              headers.push(chartCsvHeaders.fanPlenumEMC);
            }
            
            headers.push(chartCsvHeaders.fanAmbientTempF);
            headers.push(chartCsvHeaders.fanAmbientRH);
            headers.push(chartCsvHeaders.fanAmbientAH);
            headers.push(chartCsvHeaders.fanAmbientDP)
            if (useRHForFanControl === false) {
              headers.push(chartCsvHeaders.fanAmbientEMC);
            }
        headers.push(...(layerSummaryTemperatureHistoryQuery.data?.map(column => `${column.label} Temp F` as string) ?? []));

      const data: BaseCsv[] = [];

      const timeSeries = layerSummaryTemperatureHistoryQuery.data?.[0]?.data?.map(p => p.x) ?? [];
      for (let i = 0; i < (timeSeries?.length ?? 0); i++) {
        const row: BaseCsv = {
          [timestampHeader]: timeSeries[i],
          [chartCsvHeaders.fanPlenumTempF]: ambientPlenumTemperatureChartQuery.data?.[0]?.data?.[i]?.y,
          [chartCsvHeaders.fanPlenumRH]: ambientPlenumTemperatureChartQuery.data?.[4]?.data?.[i]?.y,
          [chartCsvHeaders.fanPlenumAH]: ambientPlenumTemperatureChartQuery.data?.[5]?.data?.[i]?.y,
          [chartCsvHeaders.fanPlenumDP]: ambientPlenumTemperatureChartQuery.data?.[3]?.data?.[i]?.y,
          [chartCsvHeaders.fanPlenumEMC]: ambientPlenumTemperatureChartQuery.data?.[6]?.data?.[i]?.y,
          [chartCsvHeaders.fanAmbientTempF]: ambientPlenumTemperatureChartQuery.data?.[2]?.data?.[i]?.y,
          [chartCsvHeaders.fanAmbientRH]: ambientPlenumTemperatureChartQuery.data?.[7]?.data?.[i]?.y,
          [chartCsvHeaders.fanAmbientAH]: ambientPlenumTemperatureChartQuery.data?.[8]?.data?.[i]?.y,
          [chartCsvHeaders.fanAmbientDP]: ambientPlenumTemperatureChartQuery.data?.[9]?.data?.[i]?.y,
          [chartCsvHeaders.fanAmbientEMC]: ambientPlenumTemperatureChartQuery.data?.[10]?.data?.[i]?.y,
        };
        for (let dataset of layerSummaryTemperatureHistoryQuery.data ?? []) {
          const headerName = `${dataset.label} Temp F`;
          row[headerName] = dataset.data?.[i]?.temperatureF;
        }

        data.push(row);

      }
      return {
        headers: headers, data: data
      };
    }
    if (showTemperatureChartByLayer) {
      return layerCsv();
    }

    const perSensorCcsv = () => {


      const headers: BaseHeaders = [timestampHeader];
      headers.push(chartCsvHeaders.fanPlenumTempF);
      headers.push(chartCsvHeaders.fanPlenumRH);
      headers.push(chartCsvHeaders.fanPlenumAH);
      headers.push(chartCsvHeaders.fanPlenumDP);
      if (useRHForFanControl === false) {
        headers.push(chartCsvHeaders.fanPlenumEMC);
      }
      
      headers.push(chartCsvHeaders.fanAmbientTempF);
      headers.push(chartCsvHeaders.fanAmbientRH);
      headers.push(chartCsvHeaders.fanAmbientAH);
      headers.push(chartCsvHeaders.fanAmbientDP)
      if (useRHForFanControl === false) {
        headers.push(chartCsvHeaders.fanAmbientEMC);
      }
  headers.push(...(temperatureCableIndividualSensorChartHistoryQuery.data?.map(cable => chartCsvHeaders.sensorTempF(cable.label)) ?? []));

      const data: BaseCsv[] = [];

      const timeSeries = temperatureCableIndividualSensorChartHistoryQuery.data?.[0]?.data?.map(p => p.x) ?? [];
      for (let i = 0; i < (timeSeries?.length ?? 0); i++) {
        const row: BaseCsv = {
          [chartCsvHeaders.timestamp]: timeSeries[i],
          [chartCsvHeaders.fanPlenumTempF]: ambientPlenumTemperatureChartQuery.data?.[0]?.data?.[i]?.y,
          [chartCsvHeaders.fanPlenumRH]: ambientPlenumTemperatureChartQuery.data?.[4]?.data?.[i]?.y,
          [chartCsvHeaders.fanPlenumAH]: ambientPlenumTemperatureChartQuery.data?.[5]?.data?.[i]?.y,
          [chartCsvHeaders.fanPlenumDP]: ambientPlenumTemperatureChartQuery.data?.[3]?.data?.[i]?.y,
          [chartCsvHeaders.fanPlenumEMC]: ambientPlenumTemperatureChartQuery.data?.[6]?.data?.[i]?.y,
          [chartCsvHeaders.fanAmbientTempF]: ambientPlenumTemperatureChartQuery.data?.[2]?.data?.[i]?.y,
          [chartCsvHeaders.fanAmbientRH]: ambientPlenumTemperatureChartQuery.data?.[7]?.data?.[i]?.y,
          [chartCsvHeaders.fanAmbientAH]: ambientPlenumTemperatureChartQuery.data?.[8]?.data?.[i]?.y,
          [chartCsvHeaders.fanAmbientDP]: ambientPlenumTemperatureChartQuery.data?.[9]?.data?.[i]?.y,
          [chartCsvHeaders.fanAmbientEMC]: ambientPlenumTemperatureChartQuery.data?.[10]?.data?.[i]?.y,
        };
        for (let dataset of temperatureCableIndividualSensorChartHistoryQuery.data ?? []) {
          const headerName = chartCsvHeaders.sensorTempF(dataset.label);
          row[headerName] = dataset.data?.[i]?.y;
        }

        data.push(row);

      }
      return {
        headers: headers, data: data
      };
    }

    return perSensorCcsv();
  }
  const data = flattenToCsv();

  const finalCsv = useAddDefaultAnnotationsToCsv({
    baseData: data.data,
    baseHeaders: data.headers,
    binId: props.binId, range: value,
  });


  return <>
    <div>
      <Space direction="horizontal" wrap>
        <ChartDatePicker startDate={value?.[0]!} endDate={value?.[1]!} onDateRangeChange={onDateChange} />
        <Button type="primary" disabled={inprogress > 0} icon={<RedoOutlined spin={inprogress > 0} />} onClick={() => {
          onDateChange([value?.[0]!, dayjs()], ['', '']);
        }}>
        </Button>

        <Switch unCheckedChildren="By Layer" checkedChildren="By Sensor" checked={showPerSensorChart} onChange={(checked, event) => {
          setShowPerSensorChart(checked);
        }} />

        <CsvButton data={finalCsv.data} headers={finalCsv.headers} baseFileName="temperature-cables.csv" />

      </Space>

      <div style={{ width: "100%" }}>

        {/* {[TemperatureSensorEnum.Opi, TemperatureSensorEnum.PowerCast].includes(preferredTemperatureSoruce) && <TemperatureCableOpiChart binId={props.binId} deviceId={props.deviceId} onChange={onDateChange} value={value} />} */}

        {!showPerSensorChart && <LayerTemperatureHistoryChart binId={props.binId} deviceId={props.deviceId} onChange={onDateChange} value={value} chartHeight={"60vh"} useRHForFanControl={useRHForFanControl!} preferredSensorType={preferredTemperatureSource} />}
        {showPerSensorChart && [TemperatureSensorEnum.Thermocouple, TemperatureSensorEnum.DigitalTemp].includes(preferredTemperatureSource) && <>
          <TemperatureCableChart value={value} binId={props.binId} deviceId={props.deviceId} chartHeight={"60vh"} />
        </>}
        {/* TODO: BinSense temperature chart support/query */}
        {showPerSensorChart && [TemperatureSensorEnum.Opi, TemperatureSensorEnum.PowerCast, TemperatureSensorEnum.BinSense].includes(preferredTemperatureSource) && <>
          <TemperatureCableOpiChart value={value} binId={props.binId} deviceId={props.deviceId} useRHForFanControl={useRHForFanControl!} chartHeight={"60vh"} />
        </>}

      </div>
    </div>
  </>

}